/* ===== Role Switching & Admin Pass ===== */
import { $ } from '../utils/helpers.js?v=82.86';
import { showSnackbar } from '../utils/ui.js?v=82.86';
import { sha256 } from '../utils/helpers.js?v=82.86';
import { getCurrentUser } from '../services/auth.js?v=82.86';
import { getCurrentSettings, loadSettings } from '../services/settings.js?v=82.86';
import { showPage } from './routing.js?v=82.86';
import { updateRoleCards } from './role-selection.js?v=82.86';
import { auth } from '../config/firebase.js?v=82.86';
import { logger } from '../utils/logger.js?v=82.86';
import { getPublicClientSlug } from '../services/public-client.js?v=82.86';

/** Adresa publică; dacă lipsește, o creăm automat din numele locației. */
async function ensureSlug() {
  let slug = String((getCurrentSettings() || {}).publicSlug || '').trim();
  if (slug) return slug;
  try {
    const { ensurePublicSlugBackend } = await import('../services/backend.js?v=82.86');
    const r = (await ensurePublicSlugBackend()) || {};
    slug = String(r.slug || '').trim();
    const s = getCurrentSettings();
    if (slug && s) s.publicSlug = slug;
    if (slug && r.created) showSnackbar(`Pagina ta are acum adresa /m/${slug} — o poți schimba din Cont.`, 'success');
  } catch (e) {
    logger.warn('[ensureSlug] failed', e);
  }
  return slug;
}

/**
 * „Vezi live”: deschide pagina publică reală (/m/slug) într-o filă nouă — din ea se revine simplu
 * (închizi fila), iar adminul rămâne logat. Fără slug → modul client din aplicație.
 */
export async function openPublicPage() {
  const slug = await ensureSlug();
  if (!slug) {
    showSnackbar('Pagina ta nu are încă adresă publică. Îți arăt modul client.');
    return goClient();
  }
  const url = `${window.location.origin}/m/${encodeURIComponent(slug)}`;
  // Fără „noopener” în window.open: cu el funcția întoarce mereu null și deschideam pagina și în fila curentă.
  const w = window.open(url, '_blank');
  if (w) {
    try { w.opener = null; } catch (_) { /* ignore */ }
  } else {
    // fereastra nouă a fost blocată (ex. aplicație instalată) → deschidem pagina în aceeași fereastră
    window.location.assign(url);
  }
}

/** Buton vizibil de ieșire din modul client (fără PIN). */
function ensureClientExitButton() {
  if (document.getElementById('sjClientExit')) return;
  const b = document.createElement('button');
  b.type = 'button';
  b.id = 'sjClientExit';
  b.setAttribute('aria-label', 'Înapoi la admin');
  b.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg><span>Admin</span>';
  b.addEventListener('click', () => { backToRoles(); });
  document.body.appendChild(b);
}

export async function goClient() {
  if (typeof window !== 'undefined') {
    window.__aiLayoutPreviewActive = false;
  }
  const currentUser = getCurrentUser();
  if (!currentUser?.uid) {
    showSnackbar('Loghează-te!');
    return;
  }

  let currentSettings = getCurrentSettings();
  if (!currentSettings) {
    await loadSettings(currentUser.uid, false);
    currentSettings = getCurrentSettings();
  }

  // Modul client = pagina publică (/m/slug), fără PIN. Tableta se blochează din sistem (fixare ecran / Guided Access).
  const slug = await ensureSlug();
  if (slug) {
    const url = `${window.location.origin}/m/${encodeURIComponent(slug)}`;
    const w = window.open(url, '_blank');
    if (w) {
      try { w.opener = null; } catch (_) { /* ignore */ }
    } else {
      window.location.assign(url);
    }
    return;
  }

  // Fără adresă publică încă → afișăm modul client în aplicație
  loadSettings(currentUser.uid, true)
    .then(async () => {
      const { applyClientUI } = await import('./slideshow.js?v=82.86');
      applyClientUI();
    })
    .catch(err => {
      logger.warn('[goClient] Background settings reload failed (non-critical):', err);
    });

  const campaignStatus = currentSettings?.campaignStatus || 'draft';
  const loyaltyOn =
    currentSettings?.loyaltySettings?.active === true && campaignStatus !== 'active';
  const eventOn =
    currentSettings?.eventModule?.enabled === true &&
    campaignStatus !== 'active' &&
    !(currentSettings?.loyaltySettings?.active === true);
  if (campaignStatus !== 'active' && !loyaltyOn && !eventOn) {
    showSnackbar('Activează Campanie, Fidelizare sau Evenimente din Interfață Client.');
  }

  ensureClientExitButton();
  showPage('clientPage');
  try {
    const analytics = await import('../services/client-analytics.js?v=82.86');
    analytics.trackClientInterfaceViewOnce();
  } catch (_) {
    /* ignore */
  }
  try {
    const loyaltySvc = await import('../services/loyalty.js?v=82.86');
    await loyaltySvc.ensureLoyaltySettingsLoaded(true);
      const { applyClientUI } = await import('./slideshow.js?v=82.86');
      applyClientUI();
    import('./mod-client-modules.js?v=82.86')
      .then((m) => m.refreshModClientModulesAsync())
      .catch(() => {});
  } catch (err) {
    logger.warn('[goClient] applyClientUI failed:', err);
  }
}

// Update role cards when settings are loaded
export function refreshRoleCards() {
  updateRoleCards();
}

export async function backToRoles() {
  if (typeof window !== 'undefined' && window.__aiLayoutPreviewActive === true) {
    const { exitAiLayoutPreview } = await import('../services/admin.js?v=82.86');
    exitAiLayoutPreview?.();
    showPage('adminPage');
    return;
  }
  if (getPublicClientSlug()) {
    return;
  }

  // Fără PIN: modul client din aplicație revine direct în Admin
  closeAdminPassPopup();
  document.getElementById('sjClientExit')?.remove();
  showPage('adminPage');
  updateRoleCards();
}

// Update popup UI based on whether password needs to be set or entered
function updateAdminPassPopupUI(isFirstTime) {
  const titleEl = document.querySelector('#adminPassPopup h2');
  const descEl = document.querySelector('#adminPassPopup p');
  const placeholderEl = $('adminPassInput');
  const tipEl = document.querySelector('#adminPassPopup .smallnote');
  const btnEl = document.querySelector('#adminPassPopup .btn-ghost');
  
  if (isFirstTime) {
    // First time: set password
    if (titleEl) titleEl.textContent = 'Alege un PIN pentru tabletă';
    if (descEl) descEl.textContent = 'Doar cu el se poate ieși din Modul client înapoi în Admin.';
    if (placeholderEl) placeholderEl.placeholder = 'PIN (min. 4 caractere)';
    if (tipEl) tipEl.textContent = 'Îl poți schimba oricând din Cont.';
    if (btnEl) btnEl.textContent = 'Salvează';
  } else {
    // Subsequent times: enter existing password
    if (titleEl) titleEl.textContent = 'Parola admin';
    if (descEl) descEl.textContent = 'Introdu parola admin ca să revii la roluri.';
    if (placeholderEl) placeholderEl.placeholder = 'Parola admin';
    if (tipEl) tipEl.textContent = 'Tip: parola e cea setată în Admin → „Parolă Admin".';
    if (btnEl) btnEl.textContent = 'OK';
  }
}

export function closeAdminPassPopup() {
  const popup = $('adminPassPopup');
  if (popup) popup.classList.remove('active');
}

export async function confirmAdminPass() {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    return showSnackbar('Nu ești logat!');
  }

  // Ensure settings are loaded
  await loadSettings(currentUser.uid, false);
  const currentSettings = getCurrentSettings();
  const savedHash = String(currentSettings?.adminPassHash || '').trim();
  const inputEl = $('adminPassInput');
  const input = String(inputEl?.value || '').trim();
  const popupBtn = document.querySelector('#adminPassPopup .btn-ghost');
  const originalBtnText = popupBtn ? popupBtn.textContent : 'Salvează';

  if (!input) {
    return showSnackbar('Introdu parola.');
  }

  // FIRST TIME: Set password if it doesn't exist
  if (!savedHash) {
    if (input.length < 4) {
      return showSnackbar('Parola trebuie să aibă minim 4 caractere.');
    }
    
    try {
      if (popupBtn) {
        popupBtn.disabled = true;
        popupBtn.style.pointerEvents = 'none';
        popupBtn.classList.add('btn-disabled-state');
        popupBtn.innerHTML = '<span class="inline-spinner"></span> Se salvează...';
      }

      // Use Cloud Function instead of direct Firestore write
      const { saveCampaignSettingsBackend } = await import('../services/backend.js?v=82.86');
      const passHash = await sha256(input);
      
      // Save password hash via Cloud Function
      await saveCampaignSettingsBackend({
        adminPassHash: passHash
      });
      
      // Update local settings
      currentSettings.adminPassHash = passHash;
      
      showSnackbar('PIN setat! ✔', 'success');
      closeAdminPassPopup();
      if (window.__sjPinThenClient) {
        window.__sjPinThenClient = false;
        goClient();
        return;
      }
      showPage('adminPage');
      
      // Refresh role cards to reflect admin access
      updateRoleCards();
    } catch (e) {
      if (popupBtn) {
        popupBtn.disabled = false;
        popupBtn.style.pointerEvents = '';
        popupBtn.classList.remove('btn-disabled-state');
        popupBtn.textContent = originalBtnText;
      }
      logger.error('[confirmAdminPass] Error setting password:', e);
      showSnackbar('Eroare la setarea parolei: ' + e.message, 'error');
    }
    return;
  }

  // SUBSEQUENT TIMES: Validate existing password
  const inputHash = await sha256(input);
  if (inputHash === savedHash) {
    closeAdminPassPopup();
    showPage('adminPage');
  } else {
    showSnackbar('Parolă greșită!', 'error');
    // Clear input on error
    if (inputEl) inputEl.value = '';
  }
}

// Enter key listener for admin pass popup
document.addEventListener('keydown', (e) => {
  const popup = $('adminPassPopup');
  if (e.key === 'Enter' && popup?.classList.contains('active')) {
    confirmAdminPass();
  }
});

// Open change admin password popup
export function openChangeAdminPasswordPopup() {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    return showSnackbar('Nu ești logat!', 'error');
  }

  const popup = $('changeAdminPasswordPopup');
  if (!popup) return;

  // Reset inputs
  const oldInput = $('changeAdminPasswordOld');
  const newInput = $('changeAdminPasswordNew');
  if (oldInput) {
    oldInput.value = '';
    oldInput.type = 'password';
    const toggle1 = oldInput.parentElement?.querySelector('.reset-pin-toggle');
    if (toggle1) toggle1.classList.remove('is-visible');
  }
  if (newInput) {
    newInput.value = '';
    newInput.type = 'password';
    const toggle2 = newInput.parentElement?.querySelector('.reset-pin-toggle');
    if (toggle2) toggle2.classList.remove('is-visible');
  }

  popup.classList.add('active');
  if (oldInput) oldInput.focus();
}

// Close change admin password popup
export function closeChangeAdminPasswordPopup() {
  const popup = $('changeAdminPasswordPopup');
  if (popup) popup.classList.remove('active');
  
  // Clear inputs
  const oldInput = $('changeAdminPasswordOld');
  const newInput = $('changeAdminPasswordNew');
  if (oldInput) {
    oldInput.value = '';
    oldInput.type = 'password';
    const toggle1 = oldInput.parentElement?.querySelector('.reset-pin-toggle');
    if (toggle1) toggle1.classList.remove('is-visible');
  }
  if (newInput) {
    newInput.value = '';
    newInput.type = 'password';
    const toggle2 = newInput.parentElement?.querySelector('.reset-pin-toggle');
    if (toggle2) toggle2.classList.remove('is-visible');
  }
}

// Confirm change admin password
export async function confirmChangeAdminPassword() {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    return showSnackbar('Nu ești logat!', 'error');
  }

  await loadSettings(currentUser.uid, false);
  const currentSettings = getCurrentSettings();
  const savedHash = String(currentSettings?.adminPassHash || '').trim();

  if (!savedHash) {
    return showSnackbar('Nu există parolă setată. Setează una în popup-ul care apare când încerci să revii la roluri.', 'error');
  }

  const oldInput = $('changeAdminPasswordOld');
  const newInput = $('changeAdminPasswordNew');
  const oldPass = String(oldInput?.value || '').trim();
  const newPass = String(newInput?.value || '').trim();

  if (!oldPass) {
    return showSnackbar('Introdu PIN-ul vechi.', 'error');
  }

  const oldHash = await sha256(oldPass);
  if (oldHash !== savedHash) {
    return showSnackbar('PIN-ul vechi este greșit!', 'error');
  }

  if (!newPass || newPass.length < 4) {
    return showSnackbar('PIN-ul nou trebuie să aibă minim 4 caractere.', 'error');
  }

  const newHash = await sha256(newPass);
  
  try {
    // Use Cloud Function instead of direct Firestore write
    const { saveCampaignSettingsBackend } = await import('../services/backend.js?v=82.86');
    await saveCampaignSettingsBackend({
      adminPassHash: newHash
    });

    // Update local settings
    currentSettings.adminPassHash = newHash;
    
    // Clear the admin password input field
    const adminPassInput = $('adminPass');
    if (adminPassInput) adminPassInput.value = '';

    showSnackbar('PIN-ul a fost schimbat cu succes! ✔', 'success');
    closeChangeAdminPasswordPopup();
  } catch (e) {
    logger.error('[confirmChangeAdminPassword] Error:', e);
    showSnackbar('Eroare la schimbarea PIN-ului: ' + e.message, 'error');
  }
}

// Change admin password function (called from Admin UI) - opens popup
export async function changeAdminPassword() {
  await openChangeAdminPasswordPopup();
}

// Toggle password visibility for change admin password popup
window.toggleChangeAdminPasswordOld = function() {
  const input = $('changeAdminPasswordOld');
  if (!input) return;
  const toggle = input.parentElement?.querySelector('.reset-pin-toggle');
  
  if (input.type === 'password') {
    input.type = 'text';
    if (toggle) toggle.classList.add('is-visible');
  } else {
    input.type = 'password';
    if (toggle) toggle.classList.remove('is-visible');
  }
};

window.toggleChangeAdminPasswordNew = function() {
  const input = $('changeAdminPasswordNew');
  if (!input) return;
  const toggle = input.parentElement?.querySelector('.reset-pin-toggle');
  
  if (input.type === 'password') {
    input.type = 'text';
    if (toggle) toggle.classList.add('is-visible');
  } else {
    input.type = 'password';
    if (toggle) toggle.classList.remove('is-visible');
  }
};

// Reset PIN with account password
export function openResetPinPopup() {
  const popup = $('resetPinPopup');
  if (!popup) return;
  
  // Reset UI to step 1
  const step1 = $('resetPinStep1');
  const step2 = $('resetPinStep2');
  const accountPassInput = $('resetPinAccountPass');
  const messageEl = $('resetPinMessage');
  
  if (step1) step1.style.display = 'block';
  if (step2) step2.style.display = 'none';
  if (accountPassInput) {
    accountPassInput.value = '';
    accountPassInput.type = 'password';
    const toggle1 = accountPassInput.parentElement?.querySelector('.reset-pin-toggle');
    if (toggle1) toggle1.classList.remove('is-visible');
  }
  if (messageEl) messageEl.textContent = 'Pentru securitate, introdu parola contului tău.';
  
  popup.classList.add('active');
  if (accountPassInput) accountPassInput.focus();
}

export function closeResetPinPopup() {
  const popup = $('resetPinPopup');
  if (popup) popup.classList.remove('active');
  
  // Clear inputs and reset visibility
  const accountPassInput = $('resetPinAccountPass');
  const newPinInput = $('resetPinNew');
  const confirmPinInput = $('resetPinConfirm');
  
  if (accountPassInput) {
    accountPassInput.value = '';
    accountPassInput.type = 'password';
    const toggle1 = accountPassInput.parentElement?.querySelector('.reset-pin-toggle');
    if (toggle1) toggle1.classList.remove('is-visible');
  }
  if (newPinInput) {
    newPinInput.value = '';
    newPinInput.type = 'password';
    const toggle2 = newPinInput.parentElement?.querySelector('.reset-pin-toggle');
    if (toggle2) toggle2.classList.remove('is-visible');
  }
  if (confirmPinInput) {
    confirmPinInput.value = '';
    confirmPinInput.type = 'password';
    const toggle3 = confirmPinInput.parentElement?.querySelector('.reset-pin-toggle');
    if (toggle3) toggle3.classList.remove('is-visible');
  }
}

export async function confirmResetPinAccountPass() {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    return showSnackbar('Nu ești logat!');
  }

  const accountPassInput = $('resetPinAccountPass');
  const accountPass = String(accountPassInput?.value || '').trim();
  
  if (!accountPass) {
    return showSnackbar('Introdu parola contului.');
  }

  try {
    // Re-authenticate with Firebase
    const cred = firebase.auth.EmailAuthProvider.credential(currentUser.email, accountPass);
    await currentUser.reauthenticateWithCredential(cred);
    
    // Authentication successful - show step 2
    const step1 = $('resetPinStep1');
    const step2 = $('resetPinStep2');
    const newPinInput = $('resetPinNew');
    const confirmPinInput = $('resetPinConfirm');
    
    if (step1) step1.style.display = 'none';
    if (step2) step2.style.display = 'block';
    if (newPinInput) {
      newPinInput.value = '';
      newPinInput.type = 'password';
      const toggle2 = newPinInput.parentElement?.querySelector('.reset-pin-toggle');
      if (toggle2) toggle2.classList.remove('is-visible');
      newPinInput.focus();
    }
    if (confirmPinInput) {
      confirmPinInput.value = '';
      confirmPinInput.type = 'password';
      const toggle3 = confirmPinInput.parentElement?.querySelector('.reset-pin-toggle');
      if (toggle3) toggle3.classList.remove('is-visible');
    }
  } catch (e) {
    logger.error('[confirmResetPinAccountPass] Re-auth error:', e);
    showSnackbar('Parolă cont incorectă.');
    if (accountPassInput) accountPassInput.value = '';
  }
}

export async function saveResetPin() {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    return showSnackbar('Nu ești logat!');
  }

  const newPinInput = $('resetPinNew');
  const confirmPinInput = $('resetPinConfirm');
  
  const newPin = String(newPinInput?.value || '').trim();
  const confirmPin = String(confirmPinInput?.value || '').trim();
  
  if (!newPin || newPin.length < 4) {
    return showSnackbar('PIN-ul trebuie să aibă minim 4 caractere.');
  }
  
  if (newPin !== confirmPin) {
    return showSnackbar('PIN-urile nu coincid.');
  }

  try {
    // Hash the new PIN
    const newPinHash = await sha256(newPin);
    
    // Use Cloud Function instead of direct Firestore write
    const { saveCampaignSettingsBackend } = await import('../services/backend.js?v=82.86');
    await saveCampaignSettingsBackend({
      adminPassHash: newPinHash
    });

    // Update local settings
    const { getCurrentSettings } = await import('../services/settings.js?v=82.86');
    const currentSettings = getCurrentSettings();
    if (currentSettings) {
      currentSettings.adminPassHash = newPinHash;
    }
    
    // Clear the admin password input field
    const adminPassInput = $('adminPass');
    if (adminPassInput) adminPassInput.value = '';
    
    showSnackbar('PIN resetat cu succes. ✔', 'success');
    closeResetPinPopup();
  } catch (e) {
    logger.error('[saveResetPin] Error:', e);
    showSnackbar('Eroare la resetarea PIN-ului: ' + e.message, 'error');
  }
}

// Toggle password visibility for reset PIN popup
window.toggleResetPinPassword = function() {
  const input = $('resetPinAccountPass');
  if (!input) return;
  const toggle = input.parentElement?.querySelector('.reset-pin-toggle');
  
  if (input.type === 'password') {
    input.type = 'text';
    if (toggle) toggle.classList.add('is-visible');
  } else {
    input.type = 'password';
    if (toggle) toggle.classList.remove('is-visible');
  }
};

window.toggleResetPinNew = function() {
  const input = $('resetPinNew');
  if (!input) return;
  const toggle = input.parentElement?.querySelector('.reset-pin-toggle');
  
  if (input.type === 'password') {
    input.type = 'text';
    if (toggle) toggle.classList.add('is-visible');
  } else {
    input.type = 'password';
    if (toggle) toggle.classList.remove('is-visible');
  }
};

window.toggleResetPinConfirm = function() {
  const input = $('resetPinConfirm');
  if (!input) return;
  const toggle = input.parentElement?.querySelector('.reset-pin-toggle');
  
  if (input.type === 'password') {
    input.type = 'text';
    if (toggle) toggle.classList.add('is-visible');
  } else {
    input.type = 'password';
    if (toggle) toggle.classList.remove('is-visible');
  }
};

// Enter key listeners for reset PIN popup
document.addEventListener('DOMContentLoaded', () => {
  const accountPassInput = $('resetPinAccountPass');
  if (accountPassInput) {
    accountPassInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        confirmResetPinAccountPass();
      }
    });
  }
  
  const confirmPinInput = $('resetPinConfirm');
  if (confirmPinInput) {
    confirmPinInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        saveResetPin();
      }
    });
  }
});

// Make available globally for onclick handlers
window.goClient = goClient;
window.openPublicPage = openPublicPage;
window.backToRoles = backToRoles;
window.closeAdminPassPopup = closeAdminPassPopup;
window.confirmAdminPass = confirmAdminPass;
window.changeAdminPassword = changeAdminPassword;
window.openChangeAdminPasswordPopup = openChangeAdminPasswordPopup;
window.closeChangeAdminPasswordPopup = closeChangeAdminPasswordPopup;
window.confirmChangeAdminPassword = confirmChangeAdminPassword;
window.openResetPinPopup = openResetPinPopup;
window.closeResetPinPopup = closeResetPinPopup;
window.confirmResetPinAccountPass = confirmResetPinAccountPass;
window.saveResetPin = saveResetPin;
